/**
 * useApi — a reusable data-fetching hook
 * ======================================
 * Tracks the three states every data screen needs — loading / data / error —
 * and exposes a `reload()` to re-run the fetch (e.g. a Retry button).
 *
 *     const { data, loading, error, reload } = useApi(getHoroscopeFeed)
 */
import { useCallback, useEffect, useState } from 'react'

export interface ApiState<T> {
  data: T | null
  loading: boolean
  error: Error | null
}

export interface UseApiResult<T> extends ApiState<T> {
  /** Re-run the fetcher (resets to loading, then data/error). */
  reload: () => void
}

export function useApi<T>(
  fetcher: (signal?: AbortSignal) => Promise<T>,
  deps: unknown[] = []
): UseApiResult<T> {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: true,
    error: null,
  })
  // Bumping this re-runs the effect below → a fresh fetch.
  const [reloadNonce, setReloadNonce] = useState(0)
  const reload = useCallback(() => setReloadNonce((n) => n + 1), [])

  useEffect(() => {
    const controller = new AbortController()
    setState({ data: null, loading: true, error: null })

    fetcher(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setState({ data, loading: false, error: null })
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return // we cancelled it on purpose
        setState({
          data: null,
          loading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        })
      })

    // Cleanup: if the component unmounts (or we reload) mid-request, cancel it.
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadNonce])

  return { ...state, reload }
}
